import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { PropsWithChildren } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

import { useGuideTour } from "../../../packages/guide-tour/src";
import { GuideTourHost } from "../../guide-tour/GuideTourHost";
import {
  getGuideTourModulesForUser,
  getGuideTourRouteStartStepIndex,
  getGuideTourStartStepIndex,
} from "../../guide-tour/guideTourConfig";
import { loadGuideTourPersistenceSnapshot } from "../../guide-tour/guideTourStorage";
import { useActivePurchaseAccess } from "../../hooks/useActivePurchaseAccess";
import { useAuth } from "../../hooks/useAuth";
import { useNotificationBell } from "../../hooks/useNotificationBell";
import { useOfficeVesselScope } from "../../hooks/useOfficeVesselScope";
import { canManageContractApprovalChains, canViewContracts } from "../../lib/contractAccess";
import {
  applyOfficeVesselScopeToHref,
  applyOfficeVesselScopeToSearchParams,
  isOfficeVesselScopedPath,
} from "../../lib/officeVesselScope";
import {
  canManageApprovalChains as canManageApprovalChainConfig,
  canManageInventoryAdministration,
  canManageInvoiceConfiguration,
  canViewAccountCodes,
  canViewBudgets,
  canViewCreditNotes,
  canViewExchangeRates as canViewExchangeRatesRegister,
  canViewRFQWorklist,
  canViewItems,
  canViewServices,
  canViewInvoices,
  canViewVendors,
} from "../../lib/purchaseAccess";
import { FORM_IDS, PROCESS_IDS } from "../../lib/permissionCatalog";
import { hasPermission } from "../../lib/permissions";
import { normalizeVesselId } from "../../lib/vesselIds";
import { getAttentionRegisterRequestedVessel } from "../../lib/attentionRegister";
import { useOfflineStatus } from "../../pwa/hooks/useOfflineStatus";
import { canAccessAudit } from "../audit/auditAccess";
import { getAccessibleReportSections } from "../reports/reportAccess";
import { NotificationBell } from "../notifications/NotificationBell";
import { OfficeVesselScopeSelect } from "./OfficeVesselScopeSelect";
import { OfflineIndicator } from "./OfflineIndicator";
import type { AuthUser } from "../../types/auth";
import type { GuideTourModule } from "../../guide-tour/types";

const PRIMARY_NAV_SCROLL_STORAGE_KEY = "purchase-module:primary-navigation-scroll-top";

export function revealActiveSidebarItem(
  scrollContainer: globalThis.HTMLElement,
  activeItem: globalThis.HTMLElement,
) {
  const containerRect = scrollContainer.getBoundingClientRect();
  const activeItemRect = activeItem.getBoundingClientRect();
  if (activeItemRect.top < containerRect.top) {
    scrollContainer.scrollTop -= containerRect.top - activeItemRect.top;
  } else if (activeItemRect.bottom > containerRect.bottom) {
    scrollContainer.scrollTop += activeItemRect.bottom - containerRect.bottom;
  }
}

interface NavItem {
  label: string;
  to: string;
  show: boolean;
  badgeCount?: number;
  icon:
    | "home"
    | "clipboard"
    | "file"
    | "building"
    | "cart"
    | "truck"
    | "receipt"
    | "credit"
    | "budget"
    | "currency"
    | "audit"
    | "users"
    | "settings"
    | "shield"
    | "grn"
    | "guide";
  matches?: (pathname: string, search: string) => boolean;
}

function getNavItemClass(isActive: boolean) {
  return isActive
    ? "bg-blue-50 text-blue-600"
    : "text-gray-700 hover:bg-gray-50 hover:text-gray-900";
}

function getGuideTourModuleClass(isActive: boolean) {
  return isActive
    ? "bg-blue-50 text-blue-700"
    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900";
}

function getDisplayName(username: string | null | undefined, fallback?: string | null) {
  return fallback && fallback.trim().length > 0 ? fallback : (username ?? "Authenticated User");
}

function formatRoleLabel(role: string | null | undefined) {
  return role ? role.replace(/_/g, " ") : "Authenticated Session";
}

const ANDROID_APP_DOWNLOAD_HREF = "/downloads/ksm-purchase-mobile.apk";

function getAssignedVesselName(user: AuthUser | null | undefined) {
  const vesselName = user?.vessel_name?.trim();
  if (vesselName) {
    return vesselName;
  }
  const scopedVesselName = user?.vessels?.[0]?.vessel_name?.trim();
  return scopedVesselName || null;
}

function buildRoleLabel(user: AuthUser | null | undefined) {
  const baseRoleLabel =
    user?.type === "crew"
      ? formatRoleLabel(user.rank ?? user.role_name ?? user.role ?? null)
      : formatRoleLabel(user?.role_name ?? user?.role ?? user?.rank ?? null);

  if (user?.type !== "crew") {
    return baseRoleLabel;
  }

  const vesselName = getAssignedVesselName(user);
  return vesselName ? `${baseRoleLabel} (${vesselName})` : baseRoleLabel;
}

function NavIcon({ icon, isActive }: { icon: NavItem["icon"]; isActive: boolean }) {
  const className = isActive ? "text-blue-600" : "text-gray-400 group-hover:text-gray-500";

  switch (icon) {
    case "home":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M4 10.5L12 4l8 6.5V20a1 1 0 01-1 1h-4.5v-6h-5v6H5a1 1 0 01-1-1v-9.5z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "clipboard":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M9 4h6l1 2h2a1 1 0 011 1v12a2 2 0 01-2 2H7a2 2 0 01-2-2V7a1 1 0 011-1h2l1-2zm-1 7h8M8 15h8"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "file":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M7 3h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2zm7 1v5h5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "building":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M4 21V5a1 1 0 011-1h8a1 1 0 011 1v16M9 8h1m-1 4h1m-1 4h1m4-4h5m-5 4h5m-5-8h5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "cart":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M4 5h2l2.4 9.5a1 1 0 001 .75h7.9a1 1 0 001-.8L20 8H7.2M10 19a1 1 0 100 2 1 1 0 000-2zm7 0a1 1 0 100 2 1 1 0 000-2z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "truck":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M3 7h11v8H3V7zm11 3h3l3 3v2h-6v-5zm-7 7a1 1 0 100 2 1 1 0 000-2zm10 0a1 1 0 100 2 1 1 0 000-2z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "receipt":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M7 3h10v18l-2-1.5L12 21l-3-1.5L7 21V3zm3 5h4M10 12h4M10 16h4"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "credit":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M4 7h16a1 1 0 011 1v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a1 1 0 011-1zm0 4h17M7 16h4"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "budget":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M6 20V10m6 10V4m6 16v-7M4 20h16"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "currency":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M12 4v16m4-12.5c-.8-1-2.1-1.5-4-1.5-2.5 0-4 1.2-4 3s1.7 2.7 4 3c2.3.3 4 1.1 4 3s-1.5 3-4 3c-2 0-3.4-.6-4.3-1.8"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "audit":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M8 7V5a4 4 0 118 0v2m-9 0h10a1 1 0 011 1v10a2 2 0 01-2 2H8a2 2 0 01-2-2V8a1 1 0 011-1z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "users":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M16 21v-1a4 4 0 00-4-4H7a4 4 0 00-4 4v1m11-11a3 3 0 110-6 3 3 0 010 6zm6 10v-1a3 3 0 00-2-2.8M17 4.5a3 3 0 010 5.8"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "settings":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M12 8.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zm8 3.5l-1.6-.6a6.9 6.9 0 00-.5-1.2l.7-1.6-1.9-1.9-1.6.7c-.4-.2-.8-.4-1.2-.5L13 3h-2l-.6 1.6c-.4.1-.8.3-1.2.5l-1.6-.7-1.9 1.9.7 1.6c-.2.4-.4.8-.5 1.2L4 12v2l1.6.6c.1.4.3.8.5 1.2l-.7 1.6 1.9 1.9 1.6-.7c.4.2.8.4 1.2.5L11 21h2l.6-1.6c.4-.1.8-.3 1.2-.5l1.6.7 1.9-1.9-.7-1.6c.2-.4.4-.8.5-1.2L20 14v-2z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.4"
          />
        </svg>
      );
    case "shield":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M12 3l7 3v5c0 4.5-2.9 8.4-7 10-4.1-1.6-7-5.5-7-10V6l7-3z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "grn":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M7 4h10l2 3v12a1 1 0 01-1 1H6a1 1 0 01-1-1V7l2-3zm2 9l2 2 4-4"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "guide":
      return (
        <svg aria-hidden="true" className={`h-5 w-5 ${className}`} fill="none" viewBox="0 0 24 24">
          <path
            d="M12 3l2.4 5 5.6.8-4 3.9.9 5.5-4.9-2.6-4.9 2.6.9-5.5-4-3.9 5.6-.8L12 3zm0 13.2V20"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
  }
}

export function AppShell({ children }: PropsWithChildren) {
  const { logout, user } = useAuth();
  const { commentUnreadCount, unreadCount } = useNotificationBell();
  const { user: activePurchaseUser } = useActivePurchaseAccess();
  const {
    isEnabled: officeVesselScopeEnabled,
    selectedVesselId,
    setSelectedVesselId,
    vesselOptions,
  } = useOfficeVesselScope();
  const {
    activeModule: activeGuideTourModule,
    isTourActive: isGuideTourActive,
    startTour,
  } = useGuideTour<GuideTourModule>();
  const { isOnline } = useOfflineStatus();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isGuideTourMenuOpen, setIsGuideTourMenuOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [syncErrorMessage, setSyncErrorMessage] = useState<string | null>(null);
  const mobileNavButtonRef = useRef<globalThis.HTMLButtonElement>(null);
  const mobileNavCloseButtonRef = useRef<globalThis.HTMLButtonElement>(null);
  const mobileNavPanelRef = useRef<globalThis.HTMLElement>(null);
  const primaryNavScrollRef = useRef<globalThis.HTMLDivElement>(null);
  const isOfficeUser = user?.type === "office";
  const purchaseAccessUser = activePurchaseUser ?? user;
  const withOfficeScope = (to: string) =>
    isOfficeUser && officeVesselScopeEnabled
      ? applyOfficeVesselScopeToHref(to, selectedVesselId)
      : to;
  const homePath = withOfficeScope("/dashboard");
  const reportSections = getAccessibleReportSections(purchaseAccessUser);
  const displayName = getDisplayName(
    user?.display_name ??
      user?.full_name ??
      [user?.first_name, user?.surname].filter(Boolean).join(" "),
    user?.username ?? user?.UserName ?? null,
  );
  const roleLabel = buildRoleLabel(user);
  const canManageAdminUsers =
    isOfficeUser &&
    hasPermission(user, {
      forms: [FORM_IDS.ADMIN_USERS],
      processes: [PROCESS_IDS.ADMIN_USER_MANAGE],
    });
  const canManageSystemConfig =
    isOfficeUser &&
    hasPermission(user, {
      forms: [FORM_IDS.SYSTEM_CONFIG],
      processes: [PROCESS_IDS.SYSTEM_CONFIG_MANAGE],
    });
  const canManageInvoiceConfig = canManageInvoiceConfiguration(user);
  const canViewExchangeRates = canViewExchangeRatesRegister(user);
  const canViewBudgetRegister = canViewBudgets(purchaseAccessUser);
  const canManageApprovalChains = canManageApprovalChainConfig(user);
  const canViewAccountCodeRegister = canViewAccountCodes(user);
  const canViewCreditNotesRegister = canViewCreditNotes(purchaseAccessUser);
  const canViewContractRegister = canViewContracts(purchaseAccessUser);
  const canManageContractChains = canManageContractApprovalChains(user);
  const guideTourModules = useMemo(() => getGuideTourModulesForUser(user), [user]);
  const canStartGuideTour = guideTourModules.length > 0;

  const navItems: NavItem[] = [
    {
      label: "Home",
      to: homePath,
      show: hasPermission(user, { forms: [FORM_IDS.DASHBOARD] }),
      icon: "home",
      matches: (currentPathname) => currentPathname === "/dashboard",
    },
    {
      label: "My Comments",
      to: "/my-comments",
      show: Boolean(user),
      badgeCount: commentUnreadCount ?? 0,
      icon: "file",
      matches: (currentPathname) => currentPathname.startsWith("/my-comments"),
    },
    {
      label: "Requisitions",
      to: withOfficeScope("/requisitions"),
      show: hasPermission(purchaseAccessUser, { forms: [FORM_IDS.REQUISITIONS] }),
      icon: "clipboard",
      matches: (currentPathname) => currentPathname.startsWith("/requisitions"),
    },
    {
      label: "Lube Oil Charts",
      to: "/lube-oil-charts",
      show: hasPermission(purchaseAccessUser, {
        forms: [FORM_IDS.LUBE_OIL_CHARTS],
        processes: [PROCESS_IDS.LUBE_CHART_VIEW],
      }),
      icon: "file",
      matches: (currentPathname) => currentPathname.startsWith("/lube-oil-charts"),
    },
    {
      label: "RFQs",
      to: withOfficeScope("/rfqs"),
      show: canViewRFQWorklist(purchaseAccessUser),
      icon: "file",
      matches: (currentPathname) => currentPathname.startsWith("/rfqs"),
    },
    {
      label: "Vendors",
      to: "/vendors",
      show: canViewVendors(user),
      icon: "building",
      matches: (currentPathname) => currentPathname.startsWith("/vendors"),
    },
    {
      label: "Item Master",
      to: "/items",
      show: canViewItems(user),
      icon: "file",
      matches: (currentPathname) => currentPathname.startsWith("/items"),
    },
    {
      label: "Inventory",
      to: withOfficeScope("/inventory"),
      show: canManageInventoryAdministration(user),
      icon: "file",
      matches: (currentPathname) => currentPathname.startsWith("/inventory"),
    },
    {
      label: "Service Master",
      to: "/services",
      show: canViewServices(user),
      icon: "file",
      matches: (currentPathname) => currentPathname.startsWith("/services"),
    },
    {
      label: "Purchase Orders",
      to: withOfficeScope("/purchase-orders"),
      show: hasPermission(purchaseAccessUser, { forms: [FORM_IDS.PURCHASE_ORDERS] }),
      icon: "cart",
      matches: (currentPathname, currentSearch) =>
        currentPathname.startsWith("/purchase-orders") &&
        new globalThis.URLSearchParams(currentSearch).get("filter") !== "my-approvals" &&
        !currentSearch.includes("view=tracker") &&
        !currentPathname.includes("/tracker"),
    },
    {
      label: "My Approvals",
      to: "/purchase-orders?filter=my-approvals",
      show: isOfficeUser,
      icon: "cart",
      matches: (currentPathname, currentSearch) => currentPathname === "/purchase-orders"
        && new globalThis.URLSearchParams(currentSearch).get("filter") === "my-approvals",
    },
    {
      label: "Budget Residuals",
      to: withOfficeScope("/budget-residuals"),
      show:
        isOfficeUser &&
        hasPermission(purchaseAccessUser, { forms: [FORM_IDS.BUDGET_RESIDUALS] }),
      icon: "budget",
      matches: (currentPathname) => currentPathname.startsWith("/budget-residuals"),
    },
    {
      label: "Contracts",
      to: withOfficeScope("/contracts"),
      show: canViewContractRegister,
      icon: "file",
      matches: (currentPathname) => currentPathname.startsWith("/contracts"),
    },
    {
      label: "Disputes",
      to: withOfficeScope("/disputes"),
      show: isOfficeUser && hasPermission(purchaseAccessUser, { forms:[FORM_IDS.DISPUTES],processes:[PROCESS_IDS.DISPUTE_VIEW] }),
      icon: "file",
      matches: (currentPathname) => currentPathname.startsWith("/disputes"),
    },
    {
      label: "PO Tracker",
      to: withOfficeScope("/purchase-orders?view=tracker"),
      show: hasPermission(purchaseAccessUser, { forms: [FORM_IDS.TRACKER] }),
      icon: "truck",
      matches: (currentPathname, currentSearch) =>
        currentPathname !== "/purchase-orders/tracker/bulk-update" &&
        currentPathname.startsWith("/purchase-orders") &&
        (currentSearch.includes("view=tracker") || currentPathname.includes("/tracker")),
    },
    {
      label: "Bulk Tracker Update",
      to: withOfficeScope("/purchase-orders/tracker/bulk-update"),
      show: hasPermission(purchaseAccessUser, {
        forms: [FORM_IDS.TRACKER],
        processes: [PROCESS_IDS.TRACKER_UPDATE],
      }),
      icon: "truck",
      matches: (currentPathname) => currentPathname === "/purchase-orders/tracker/bulk-update",
    },
    {
      label: "Invoices",
      to: withOfficeScope("/invoices"),
      show: canViewInvoices(purchaseAccessUser),
      icon: "receipt",
      matches: (currentPathname) =>
        currentPathname.startsWith("/invoices") &&
        !currentPathname.startsWith("/invoices/configuration"),
    },
    {
      label: "Invoice Config",
      to: "/invoices/configuration",
      show: canManageInvoiceConfig,
      icon: "settings",
      matches: (currentPathname) => currentPathname.startsWith("/invoices/configuration"),
    },
    {
      label: "Credit Notes",
      to: withOfficeScope("/credit-notes"),
      show: canViewCreditNotesRegister,
      icon: "credit",
      matches: (currentPathname) => currentPathname.startsWith("/credit-notes"),
    },
    {
      label: "Owner Portal",
      to: "/owner/dashboard",
      show: hasPermission(user, {
        forms: [FORM_IDS.OWNER_PORTAL],
        processes: [PROCESS_IDS.OWNER_PORTAL_VIEW],
      }),
      icon: "home",
      matches: (currentPathname) => currentPathname.startsWith("/owner"),
    },
    {
      label: "Budgets",
      to: "/budget/plan",
      show: canViewBudgetRegister,
      icon: "budget",
      matches: (currentPathname) =>
        currentPathname.startsWith("/budget") &&
        !currentPathname.startsWith("/budget/approvals") &&
        !currentPathname.startsWith("/budget-residuals"),
    },
    {
      label: "Budget Approvals",
      to: withOfficeScope("/budget/approvals"),
      show: hasPermission(purchaseAccessUser, {
        forms: [FORM_IDS.BUDGETS],
        processes: [PROCESS_IDS.BUDGET_APPROVE],
      }),
      icon: "shield",
      matches: (currentPathname) => currentPathname.startsWith("/budget/approvals"),
    },
    {
      label: "Projects",
      to: withOfficeScope("/projects"),
      show: hasPermission(purchaseAccessUser, {
        forms: [FORM_IDS.PROJECTS],
        processes: [PROCESS_IDS.PROJECT_VIEW],
      }),
      icon: "file",
      matches: (currentPathname) => currentPathname.startsWith("/projects"),
    },
    {
      label: "Exchange Rates",
      to: "/exchange-rates",
      show: canViewExchangeRates,
      icon: "currency",
      matches: (currentPathname) => currentPathname.startsWith("/exchange-rates"),
    },
    {
      label: "Audit",
      to: "/audit",
      show: canAccessAudit(user),
      icon: "audit",
      matches: (currentPathname) => currentPathname.startsWith("/audit"),
    },
    {
      label: "Delegations",
      to: "/admin/delegations",
      show:
        user?.type === "office" &&
        hasPermission(user, {
          forms: [FORM_IDS.DELEGATIONS],
          processes: [PROCESS_IDS.DELEGATION_MANAGE],
        }),
      icon: "users",
      matches: (currentPathname) => currentPathname.startsWith("/admin/delegations"),
    },
    {
      label: "Admin Users",
      to: "/admin/users",
      show: canManageAdminUsers,
      icon: "users",
      matches: (currentPathname) => currentPathname.startsWith("/admin/users"),
    },
    {
      label: "Admin Roles",
      to: "/admin/roles",
      show: canManageAdminUsers,
      icon: "shield",
      matches: (currentPathname) => currentPathname.startsWith("/admin/roles"),
    },
    {
      label: "System Config",
      to: "/admin/system-config",
      show: canManageSystemConfig,
      icon: "settings",
      matches: (currentPathname) => currentPathname.startsWith("/admin/system-config"),
    },
    {
      label: "Account Codes",
      to: "/admin/account-codes",
      show: canViewAccountCodeRegister,
      icon: "settings",
      matches: (currentPathname) => currentPathname.startsWith("/admin/account-codes"),
    },
    {
      label: "Approval Chains",
      to: "/admin/approval-chains",
      show: canManageApprovalChains,
      icon: "settings",
      matches: (currentPathname) => currentPathname.startsWith("/admin/approval-chains"),
    },
    {
      label: "Contract Chains",
      to: "/admin/contract-approval-chains",
      show: canManageContractChains,
      icon: "settings",
      matches: (currentPathname) => currentPathname.startsWith("/admin/contract-approval-chains"),
    },
    {
      label: "GRN",
      to: withOfficeScope("/grns"),
      show: hasPermission(user, { forms: [FORM_IDS.GRN] }),
      icon: "grn",
      matches: (currentPathname) => currentPathname.startsWith("/grns"),
    },
  ];

  useEffect(() => {
    if (user?.type !== "crew") {
      void import("../../pwa/serviceWorker").then((module) =>
        module.unregisterVesselServiceWorker(),
      );
      return;
    }

    void import("../../pwa/serviceWorker").then((module) => module.registerVesselServiceWorker());
  }, [user?.type]);

  useEffect(() => {
    if (user?.type !== "crew" || !isOnline) {
      return;
    }

    void import("../../pwa/offlineSyncCoordinator")
      .then(async (module) => {
        const result = await module.runVesselOfflineSync(user);
        if (result.failedIntentId) {
          const queueModule = await import("../../pwa/indexedDB/queueStore");
          const failedIntent = await queueModule.getQueuedIntent(result.failedIntentId);
          setSyncErrorMessage(
            failedIntent?.lastError ??
              "Vessel sync could not replay one queued requisition action. Its content remains saved locally.",
          );
        } else {
          setSyncErrorMessage(null);
        }
        if ((result?.processedQueueCount ?? 0) > 0 || result.completedTiers.length > 0) {
          window.dispatchEvent(new window.CustomEvent("purchase-requisition-sync-complete"));
        }
      })
      .catch(() => {
        // Background sync should never block shell rendering.
      });
  }, [isOnline, user]);

  useEffect(() => {
    setIsUserMenuOpen(false);
    setIsMobileNavOpen(false);
  }, [pathname, search]);

  const persistPrimaryNavScrollPosition = () => {
    const scrollContainer = primaryNavScrollRef.current;
    if (!scrollContainer) return;
    try {
      window.sessionStorage.setItem(
        PRIMARY_NAV_SCROLL_STORAGE_KEY,
        String(scrollContainer.scrollTop),
      );
    } catch {
      // Navigation remains usable when browser storage is unavailable.
    }
  };

  useLayoutEffect(() => {
    const scrollContainer = primaryNavScrollRef.current;
    if (!scrollContainer) return;
    try {
      const storedScrollTop = Number.parseInt(
        window.sessionStorage.getItem(PRIMARY_NAV_SCROLL_STORAGE_KEY) ?? "0",
        10,
      );
      if (Number.isFinite(storedScrollTop) && storedScrollTop >= 0) {
        scrollContainer.scrollTop = storedScrollTop;
      }
    } catch {
      // Continue with the browser's current sidebar position.
    }

    const activeItem = scrollContainer.querySelector<globalThis.HTMLElement>(
      '[aria-current="page"]',
    );
    if (activeItem) {
      revealActiveSidebarItem(scrollContainer, activeItem);
      persistPrimaryNavScrollPosition();
    }
  }, [pathname, search]);

  useEffect(() => {
    if (!isMobileNavOpen) {
      return;
    }

    document.body.classList.add("overflow-hidden");
    mobileNavCloseButtonRef.current?.focus();

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMobileNavOpen(false);
        mobileNavButtonRef.current?.focus();
        return;
      }

      if (event.key === "Tab") {
        const focusableElements =
          mobileNavPanelRef.current?.querySelectorAll<globalThis.HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          );
        if (!focusableElements?.length) {
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileNavOpen]);

  useEffect(() => {
    if (!isOfficeUser || !officeVesselScopeEnabled || !isOfficeVesselScopedPath(pathname)) {
      return;
    }

    const currentSearchParams = new globalThis.URLSearchParams(search);
    if (getAttentionRegisterRequestedVessel(pathname, currentSearchParams) !== null) {
      return;
    }
    const currentVesselId = normalizeVesselId(currentSearchParams.get("vessel_id"));
    if (currentVesselId === selectedVesselId) {
      return;
    }

    const nextSearchParams = applyOfficeVesselScopeToSearchParams(
      currentSearchParams,
      selectedVesselId,
    );
    navigate(
      {
        pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
      },
      { replace: true },
    );
  }, [isOfficeUser, navigate, officeVesselScopeEnabled, pathname, search, selectedVesselId]);

  useEffect(() => {
    if (!activeGuideTourModule || !isGuideTourActive) {
      return;
    }

    setIsGuideTourMenuOpen(true);
  }, [activeGuideTourModule, isGuideTourActive]);

  const handleLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    try {
      await logout();
      setIsUserMenuOpen(false);
      navigate("/login", { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="app-responsive-root flex min-h-screen min-w-0 bg-gray-50">
      {isMobileNavOpen ? (
        <button
          aria-label="Close navigation overlay"
          className="fixed inset-0 z-30 bg-gray-900/50 lg:hidden"
          onClick={() => {
            setIsMobileNavOpen(false);
            mobileNavButtonRef.current?.focus();
          }}
          type="button"
        />
      ) : null}
      <aside
        ref={mobileNavPanelRef}
        aria-label="Application navigation"
        className={`fixed inset-y-0 left-0 z-40 flex w-[min(20rem,calc(100vw-2rem))] max-w-full flex-col border-r border-gray-200 bg-white pb-4 pt-3 shadow-lg transition-transform duration-200 ease-in-out lg:sticky lg:top-0 lg:z-20 lg:h-screen lg:w-64 lg:translate-x-0 lg:self-start lg:pt-5 lg:shadow-none ${isMobileNavOpen ? "translate-x-0" : "mobile-nav-closed -translate-x-full"}`}
        id="primary-navigation-panel"
      >
        <div
          ref={primaryNavScrollRef}
          className="flex flex-1 flex-col gap-6 overflow-y-auto px-4"
          onScroll={persistPrimaryNavScrollPosition}
        >
          <div className="flex min-h-11 items-center justify-between gap-3 border-b border-gray-100 pb-3 lg:hidden">
            <p className="text-sm font-semibold text-gray-900">Navigation</p>
            <button
              ref={mobileNavCloseButtonRef}
              aria-label="Close navigation"
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-600 transition hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
              onClick={() => {
                setIsMobileNavOpen(false);
                mobileNavButtonRef.current?.focus();
              }}
              type="button"
            >
              <svg aria-hidden="true" className="h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="1.8"
                />
              </svg>
            </button>
          </div>
          <nav aria-label="Primary" className="space-y-4">
            <div className="grid gap-1">
              {navItems
                .filter((item) => item.show)
                .map((item) => {
                  const isActive = item.matches ? item.matches(pathname, search) : false;
                  return (
                    <Link
                      key={item.label}
                      aria-current={isActive ? "page" : undefined}
                      className={`group flex items-center rounded-md px-3 py-2 text-sm font-medium ${getNavItemClass(
                        isActive,
                      )}`}
                      onClick={persistPrimaryNavScrollPosition}
                      to={item.to}
                    >
                      <span className="mr-3 shrink-0">
                        <NavIcon icon={item.icon} isActive={isActive} />
                      </span>
                      <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                        <span>{item.label}</span>
                        {item.badgeCount ? (
                          <span
                            aria-label={`${item.badgeCount} unread comments`}
                            className="inline-flex min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                          >
                            {item.badgeCount > 99 ? "99+" : item.badgeCount}
                          </span>
                        ) : null}
                      </span>
                    </Link>
                  );
                })}
              {canStartGuideTour ? (
                <div className="space-y-1">
                  <button
                    aria-controls="guide-tour-module-list"
                    aria-expanded={isGuideTourMenuOpen}
                    className={`group flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium ${getNavItemClass(
                      isGuideTourActive,
                    )}`}
                    data-tour-id="guide-tour-sidebar-trigger"
                    onClick={() => {
                      setIsGuideTourMenuOpen((current) => !current);
                    }}
                    type="button"
                  >
                    <span className="flex items-center">
                      <span className="mr-3 shrink-0">
                        <NavIcon icon="guide" isActive={isGuideTourActive} />
                      </span>
                      Guide Tour
                    </span>
                    <svg
                      aria-hidden="true"
                      className={`h-4 w-4 text-gray-400 transition-transform ${
                        isGuideTourMenuOpen ? "rotate-90" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M9 6l6 6-6 6"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.8"
                      />
                    </svg>
                  </button>
                  {isGuideTourMenuOpen ? (
                    <div
                      className="grid gap-1 border-l border-gray-200 pl-4"
                      id="guide-tour-module-list"
                    >
                      {guideTourModules.map((moduleDefinition) => {
                        const isModuleActive =
                          isGuideTourActive && activeGuideTourModule === moduleDefinition.key;

                        return (
                          <button
                            key={moduleDefinition.key}
                            aria-pressed={isModuleActive}
                            className={`rounded-md px-3 py-2 text-left text-sm font-medium ${getGuideTourModuleClass(
                              isModuleActive,
                            )}`}
                            onClick={() => {
                              const persistedSnapshot = loadGuideTourPersistenceSnapshot(
                                user,
                                moduleDefinition.key,
                                moduleDefinition.steps,
                              );
                              const currentRouteStepIndex = getGuideTourRouteStartStepIndex(
                                user,
                                moduleDefinition.key,
                                pathname,
                                search,
                              );

                              startTour({
                                module: moduleDefinition.key,
                                stepIndex:
                                  currentRouteStepIndex ??
                                  (persistedSnapshot.status === "in_progress"
                                    ? persistedSnapshot.stepIndex
                                    : getGuideTourStartStepIndex(
                                        user,
                                        moduleDefinition.key,
                                        pathname,
                                        search,
                                      )),
                              });
                            }}
                            type="button"
                          >
                            {moduleDefinition.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {reportSections.map((section) => (
              <section key={section.label} className="space-y-1 pt-2">
                <p className="px-3 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                  {section.label}
                </p>
                <div className="grid gap-1 border-t border-gray-100 pt-2">
                  {section.items.map((item) => {
                    const isActive = pathname === item.to;
                    return (
                      <NavLink
                        key={item.id}
                        aria-current={isActive ? "page" : undefined}
                        className={`group flex items-center rounded-md px-3 py-2 text-sm font-medium ${getNavItemClass(
                          isActive,
                        )}`}
                        onClick={persistPrimaryNavScrollPosition}
                        to={item.to}
                      >
                        <span className="mr-3 shrink-0">
                          <NavIcon icon="file" isActive={isActive} />
                        </span>
                        {item.label}
                      </NavLink>
                    );
                  })}
                </div>
              </section>
            ))}
          </nav>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="sticky top-0 z-10 shrink-0">
          <header className="flex min-h-16 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-3 py-3 sm:px-6 lg:px-8">
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
              <button
                ref={mobileNavButtonRef}
                aria-controls="primary-navigation-panel"
                aria-expanded={isMobileNavOpen}
                aria-label="Open navigation"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-600 transition hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 lg:hidden"
                onClick={() => setIsMobileNavOpen(true)}
                type="button"
              >
                <svg aria-hidden="true" className="h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path
                    d="M4 7h16M4 12h16M4 17h16"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="1.8"
                  />
                </svg>
              </button>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Operations
                </p>
                <h2 className="text-lg font-bold leading-tight text-gray-900">Purchase Module</h2>
              </div>
            </div>
            <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-x-2 gap-y-2 sm:flex-none sm:gap-x-4 lg:gap-x-6">
              {isOfficeUser && officeVesselScopeEnabled ? (
                <OfficeVesselScopeSelect
                  options={vesselOptions}
                  value={selectedVesselId}
                  onChange={setSelectedVesselId}
                />
              ) : null}
              {isOfficeUser ? (
                <a
                  className="inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-100"
                  href={ANDROID_APP_DOWNLOAD_HREF}
                  download
                >
                  Android App
                </a>
              ) : null}
              {user ? <NotificationBell unreadCount={unreadCount} /> : null}
              <div className="relative">
                <button
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="menu"
                  className="-m-1.5 flex items-center p-1.5 focus:outline-none"
                  onClick={() => {
                    setIsUserMenuOpen((current) => !current);
                  }}
                  type="button"
                >
                  <span className="sr-only">Open user menu</span>
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700">
                    {displayName
                      .split(" ")
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((part) => part[0]?.toUpperCase() ?? "")
                      .join("") || "AU"}
                  </div>
                  <span className="ml-3 hidden border-l border-gray-200 pl-4 lg:flex lg:items-center">
                    <div className="min-w-0 flex-col items-start px-1 text-left">
                      <span
                        aria-hidden="true"
                        className="block text-sm font-semibold leading-6 text-gray-900"
                      >
                        {displayName}
                      </span>
                      <span className="block truncate text-xs font-medium text-gray-500">
                        {roleLabel}
                      </span>
                    </div>
                    <svg
                      aria-hidden="true"
                      className="ml-2 h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M7 10l5 5 5-5"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.8"
                      />
                    </svg>
                  </span>
                </button>

                {isUserMenuOpen ? (
                  <div
                    className="absolute right-0 z-10 mt-2.5 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none"
                    role="menu"
                  >
                    <div className="block px-4 py-2 lg:hidden border-b border-gray-100 mb-2">
                      <p className="truncate text-sm font-semibold text-gray-900">{displayName}</p>
                      <p className="truncate text-xs font-medium text-gray-500">{roleLabel}</p>
                    </div>
                    <button
                      className="block w-full px-4 py-2 text-left text-sm font-medium text-red-600 tracking-wide transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={isLoggingOut}
                      onClick={() => {
                        void handleLogout();
                      }}
                      role="menuitem"
                      type="button"
                    >
                      {isLoggingOut ? "Signing out..." : "Logout"}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </header>
          <OfflineIndicator isOnline={isOnline} />
        </div>
        {syncErrorMessage ? (
          <div
            className="flex min-h-10 items-center justify-between gap-3 border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800"
            role="alert"
          >
            <span>{syncErrorMessage}</span>
            <button
              aria-label="Dismiss requisition sync error"
              className="shrink-0 rounded px-2 py-1 font-semibold text-red-700 hover:bg-red-100"
              onClick={() => {
                setSyncErrorMessage(null);
              }}
              type="button"
            >
              Dismiss
            </button>
          </div>
        ) : null}
        <main className="min-w-0 flex-1 bg-gray-50 px-3 py-4 sm:px-4 sm:py-6 md:px-8">
          <div key={pathname} className="mx-auto w-full max-w-[1400px]">
            {children}
          </div>
        </main>
      </div>
      <GuideTourHost />
    </div>
  );
}
